import { useTranslation } from 'react-i18next'

import { ROUTE_PATH } from 'lib/paths'

import {
  useCurrentUser,
  usePinGroupMutation,
  useUnpinGroupMutation,
} from 'store/api/yay/user'

import { matchI18nPath } from 'features/i18n/router'
import usePageModal from 'features/Modal/hooks/usePageModal'

import { MODAL_MODE } from 'components/Modal/config'
import { useReportModal } from 'components/Modal/ReportModal/ReportModal'
import { MenuOption } from 'components/Menu/Menu'

export const useGroupToolsMenu = ({
  group,
  attemptLeaveGroup,
  attemptDeleteGroup,
  onGroupToolsMenuActionCallback,
}) => {
  const { t } = useTranslation()
  const [pinGroup] = usePinGroupMutation()
  const [unpinGroup] = useUnpinGroupMutation()
  const currentUserId = useCurrentUser().data?.id
  const { pageModalData } = usePageModal()

  const reportModal = useReportModal()

  const deleteOption = {
    key: 'delete',
    title: t('common.delete'),
    icon: 'delete',
    onClick: () => {
      attemptDeleteGroup(group)
    },
  }

  const pinOption = {
    key: 'pin',
    title: t('common.pin'),
    icon: 'pin',
    onClick: async () => {
      await pinGroup(group.id)

      onGroupToolsMenuActionCallback?.({
        type: 'pin',
        groupId: group.id,
      })
    },
  }

  const unpinOption = {
    key: 'unpin',
    title: t('common.unpin'),
    icon: 'unpin',
    onClick: async () => {
      await unpinGroup(group.id)

      onGroupToolsMenuActionCallback?.({
        type: 'unpin',
      })
    },
  }

  const leaveOption = {
    key: 'leave',
    title: t('common.leave'),
    icon: 'leave',
    onClick: () => {
      attemptLeaveGroup(group)
    },
  }

  const reportOption = {
    key: 'report',
    title: t('common.report'),
    icon: 'report',
    onClick: () =>
      reportModal.open({
        userId: currentUserId.toString(),
        groupId: group.id,
      }),
  }

  const circleDetailOption = {
    key: 'detail',
    title: t('common.circle_detail'),
    to: ROUTE_PATH.GROUP_DETAIL(group.id),
  }

  const addPinOption = (menu: MenuOption[]) => {
    if (
      pageModalData.modalMode !== MODAL_MODE.RELATED_GROUPS &&
      !matchI18nPath(ROUTE_PATH.GROUPS_ALL)
    ) {
      if (group._pinned) {
        menu.push(unpinOption)
      } else {
        menu.push(pinOption)
      }
    }
  }

  const getMenu = () => {
    if (currentUserId === group?.owner?.id) {
      const menu: MenuOption[] = [deleteOption]

      addPinOption(menu)

      menu.push(circleDetailOption)

      return menu
    } else if (group?.is_joined) {
      const menu: MenuOption[] = [leaveOption, reportOption]

      addPinOption(menu)

      menu.push(circleDetailOption)

      return menu
    } else {
      return [reportOption, circleDetailOption]
    }
  }

  return { getMenu }
}
